import {
	OrderShippingMethod,
	type GetVetterJobQuery,
	type VetterJobPriceLineInput,
	type VetterDeliveryCostInput,
	type VetterShippingPriceInput,
} from '@codezo/graphql';

export type VetterJob = NonNullable<GetVetterJobQuery['getVetterJob']>;
export type VetterJobItem = VetterJob['items'][number];
export type VetterJobTier = VetterJobItem['tiers'][number];

/** Display order of the shipping methods, and their labels. */
export const SHIPPING_METHODS: { method: OrderShippingMethod; label: string }[] = [
	{ method: OrderShippingMethod.Standard, label: 'Standard' },
	{ method: OrderShippingMethod.Express, label: 'Express' },
];

export interface ShippingDraft {
	price: string;
	daysMin: string;
	daysMax: string;
}

/**
 * One line of a quantity's shipping in the form.
 *
 * When the studio has set up delivery options for the tier, the supplier
 * prices THOSE: one row per plain option and one per part of a split
 * ("10 pcs Express", "490 pcs Standard"). Until then, the two plain methods for
 * the whole quantity, as before. One shape for both, so the form and its
 * checks don't care which it is.
 */
export type ShippingRow =
	| { key: string; kind: 'method'; method: OrderShippingMethod; label: string }
	| {
			key: string;
			kind: 'option';
			deliveryId: string;
			partId: string | null;
			label: string;
			/** The split's own label ("10 Express + 490 Standard") on its first part only. */
			groupLabel: string | null;
	  };

const methodLabel = (m: OrderShippingMethod) =>
	SHIPPING_METHODS.find((x) => x.method === m)?.label ?? m;

export function shippingRowsFor(tier: VetterJobTier): ShippingRow[] {
	if (tier.deliveryOptions.length === 0) {
		return SHIPPING_METHODS.map(({ method, label }) => ({
			key: `method:${method}`,
			kind: 'method' as const,
			method,
			label,
		}));
	}
	return tier.deliveryOptions.flatMap((o): ShippingRow[] =>
		o.isSplit
			? [...o.parts]
					.sort((a, b) => a.sequence - b.sequence)
					.map((part, i) => ({
						key: `opt:${o.id}:${part.id}`,
						kind: 'option' as const,
						deliveryId: o.id,
						partId: part.id,
						label: `${part.quantity.toLocaleString('en-US')} pcs ${methodLabel(part.shippingMethod)}`,
						groupLabel: i === 0 ? o.label : null,
					}))
			: [
					{
						key: `opt:${o.id}`,
						kind: 'option' as const,
						deliveryId: o.id,
						partId: null,
						label: o.label,
						groupLabel: null,
					},
				]
	);
}

export interface TierDraft {
	unitPrice: string;
	minDays: string;
	maxDays: string;
	/** Keyed by ShippingRow.key. */
	shipping: Record<string, ShippingDraft>;
}

/** The form's figures. Its currency is the app's selected one, not held here. */
export interface PriceFormState {
	note: string;
	/** Keyed by orderQuoteItemQuantityId. */
	tiers: Record<string, TierDraft>;
}

/** Field-level messages, keyed `${tierId}.${field}`; `form` for the whole form. */
export type PriceFormErrors = Record<string, string>;

const str = (n: number | null | undefined) => (n == null ? '' : String(n));

const emptyShipping = (): ShippingDraft => ({ price: '', daysMin: '', daysMax: '' });

type SavedCost = {
	price: number;
	deliveryDaysMin?: number | null;
	deliveryDaysMax?: number | null;
} | null;

const fromCost = (c: SavedCost | undefined): ShippingDraft =>
	c
		? { price: str(c.price), daysMin: str(c.deliveryDaysMin), daysMax: str(c.deliveryDaysMax) }
		: emptyShipping();

/** A tier as last submitted — or blank. New or changed split parts come back unpriced. */
function tierDraftFor(tier: VetterJobTier, blank = false): TierDraft {
	const price = blank ? null : tier.price;
	const shipping: Record<string, ShippingDraft> = {};
	for (const row of shippingRowsFor(tier)) {
		if (blank) {
			shipping[row.key] = emptyShipping();
		} else if (row.kind === 'method') {
			shipping[row.key] = fromCost(
				price?.shippingPrices.find((s) => s.shippingMethod === row.method)
			);
		} else {
			const option = tier.deliveryOptions.find((o) => o.id === row.deliveryId);
			const cost = row.partId
				? option?.parts.find((p) => p.id === row.partId)?.cost
				: option?.cost;
			shipping[row.key] = fromCost(cost);
		}
	}
	return {
		unitPrice: str(price?.baseUnitPrice),
		minDays: str(price?.minProductionDays),
		maxDays: str(price?.maxProductionDays),
		shipping,
	};
}

const allTiers = (job: VetterJob) => job.items.flatMap((i) => i.tiers);

/** The form as last submitted — or blank. */
export function formFromJob(job: VetterJob): PriceFormState {
	const tiers: Record<string, TierDraft> = {};
	for (const tier of allTiers(job)) tiers[tier.orderQuoteItemQuantityId] = tierDraftFor(tier);
	return { note: job.note ?? '', tiers };
}

/** Accepts a decimal comma as well — suppliers type "12,50" as often as "12.50". */
function parseAmount(raw: string): number | null {
	const t = raw.trim().replace(/\s/g, '').replace(',', '.');
	if (t === '') return null;
	const n = Number(t);
	return Number.isFinite(n) ? n : NaN;
}

function parseDays(raw: string): number | null {
	const t = raw.trim();
	if (t === '') return null;
	return /^\d+$/.test(t) ? Number(t) : NaN;
}

/**
 * Days are a both-or-neither pair: a single bound says nothing a reader can
 * plan with. Returns the error, or the parsed pair.
 */
function parseDayRange(
	minRaw: string,
	maxRaw: string
): { error: string } | { min: number | null; max: number | null } {
	const min = parseDays(minRaw);
	const max = parseDays(maxRaw);
	if (Number.isNaN(min) || Number.isNaN(max)) return { error: 'Days must be whole numbers.' };
	if ((min == null) !== (max == null)) return { error: 'Give both days, or neither.' };
	if (min != null && max != null && min > max) return { error: 'From must not exceed to.' };
	return { min, max };
}

const isBlankTier = (t: TierDraft) =>
	[t.unitPrice, t.minDays, t.maxDays].every((v) => v.trim() === '') &&
	Object.values(t.shipping).every((s) =>
		[s.price, s.daysMin, s.daysMax].every((v) => v.trim() === '')
	);

/**
 * Validates the form and builds the submission. A blank tier is left out —
 * the submission REPLACES the previous one, so leaving a tier out is how a
 * supplier stops pricing it. A shipping row left empty is one they don't
 * quote; days without a price are an error, so nothing is dropped silently.
 */
export function buildSubmission(
	form: PriceFormState,
	currency: string,
	job: VetterJob
): {
	errors: PriceFormErrors;
	lines: VetterJobPriceLineInput[];
} {
	const errors: PriceFormErrors = {};
	const lines: VetterJobPriceLineInput[] = [];
	const tierById = new Map(allTiers(job).map((t) => [t.orderQuoteItemQuantityId, t]));

	for (const [tierId, draft] of Object.entries(form.tiers)) {
		const tier = tierById.get(tierId);
		if (!tier || isBlankTier(draft)) continue;

		const unit = parseAmount(draft.unitPrice);
		if (unit == null) errors[`${tierId}.unitPrice`] = 'Enter a unit price, or clear this row.';
		else if (Number.isNaN(unit) || unit <= 0)
			errors[`${tierId}.unitPrice`] = 'Enter a price above 0.';

		const production = parseDayRange(draft.minDays, draft.maxDays);
		if ('error' in production) errors[`${tierId}.days`] = production.error;

		const shippingPrices: VetterShippingPriceInput[] = [];
		const deliveryCosts: VetterDeliveryCostInput[] = [];
		for (const row of shippingRowsFor(tier)) {
			const s = draft.shipping[row.key] ?? emptyShipping();
			const key = `${tierId}.ship.${row.key}`;
			const price = parseAmount(s.price);
			if (price == null) {
				if (s.daysMin.trim() || s.daysMax.trim())
					errors[key] = 'Add a price or clear the days.';
				continue;
			}
			if (Number.isNaN(price) || price < 0) {
				errors[key] = 'Enter a shipping price of 0 or more.';
				continue;
			}
			const days = parseDayRange(s.daysMin, s.daysMax);
			if ('error' in days) {
				errors[key] = days.error;
				continue;
			}
			if (row.kind === 'method') {
				shippingPrices.push({
					shippingMethod: row.method,
					price,
					deliveryDaysMin: days.min,
					deliveryDaysMax: days.max,
				});
			} else {
				deliveryCosts.push({
					orderQuoteOfferDeliveryId: row.deliveryId,
					orderQuoteOfferDeliveryPartId: row.partId ?? undefined,
					price,
					deliveryDaysMin: days.min,
					deliveryDaysMax: days.max,
				});
			}
		}

		if (unit != null && !Number.isNaN(unit) && unit > 0 && !('error' in production)) {
			lines.push({
				orderQuoteItemQuantityId: tierId,
				baseUnitPrice: unit,
				minProductionDays: production.min,
				maxProductionDays: production.max,
				// The studio's options when it has set them up, the plain methods otherwise.
				...(tier.deliveryOptions.length > 0 ? { deliveryCosts } : { shippingPrices }),
			});
		}
	}

	if (!currency) errors.form = 'Choose the currency your prices are in.';
	else if (Object.keys(errors).length === 0 && lines.length === 0)
		errors.form = 'Price at least one quantity.';

	return { errors, lines };
}

/** The same form with every figure cleared, the note kept — for re-entering in another currency. */
export function clearedForm(form: PriceFormState, job: VetterJob): PriceFormState {
	const tiers: Record<string, TierDraft> = {};
	for (const tier of allTiers(job))
		tiers[tier.orderQuoteItemQuantityId] = tierDraftFor(tier, true);
	return { ...form, tiers };
}
